<template>
  <main class="view inquiry">
    <div class="meta">
      <router-link to="/inquiries">
        <Icon name="chevronLeft" />
        <span>Fallübersicht</span>
      </router-link>
      <div class="caseinfo">
        <Icon :name="computedCaseinfo.icon" />
        <span class="name">{{ computedCaseinfo.name }}</span>
        <span class="birthdate">{{  computedCaseinfo.date }}</span>
      </div>
      <Dropdown
        secondary
        :class="{ invisible: computedInquiryActions.length === 0 }"
        placeholder="Bearbeiten"
        ref="actionDropdown"
        :action-only="true"
        :static-placeholder="true"
        :options="computedInquiryActions"
        @input="handleInquiryAction"

      />
    </div>
    <div class="progress">
      <InquiryProgressExtended
        :status="inquiryStatus"
        :inquiry="totalFieldsProgress"
      />
    </div>
    <div class="container" ref="formContainer">
      <InteractionBar
        :actions="interactions"
        @interaction="handleInteraction"
      />
      <div class="report-container">
        <div>
          <ul>
            <li
              v-for="section in computedSections"
              :key="`section-${section.index}`"
              :class="{ active: section.index === activeSection }"
              @click="setActiveSection(section.index)">
                <span>{{ section.label }}</span>
              <ProgressCircle v-if="!section.noProgress" :progress.sync="section.progress" />
            </li>
          </ul>
        </div>
        <div :class="{ readonly: readOnly }">
          <h1 v-if="computedSectionHeading">
            {{ computedSectionHeading }}
            <Tooltip
              v-if="computedSectionTooltip"
              icon="info"
              :content="computedSectionTooltip"
            />
          </h1>
          <template v-if="computedSectionGroups.length > 0">
            <div
              class="group"
              v-for="(group, groupIndex) in computedSectionGroups"
              :key="`section-${activeSection}-group-${groupIndex}`">
                <h3 v-if="(group.label || group.tooltip) && group.children.length > 0">
                  {{ group.label }}
                  <Icon v-if="group.forState" name="fieldsState" />
                  <Tooltip v-if="group.tooltip" icon="info" :content="group.tooltip"/>
                </h3>
                <div
                  v-for="(comp) in group.children"
                  :key="comp.id"
                >
                  <Select
                    fill
                    v-if="comp.component === 'select'"
                    :label="comp.props.label"
                    :hint="comp.props.hint"
                    :tooltip="comp.props.tooltip"
                    :options="comp.props.options"
                    :searchable="comp.props.searchable"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <SelectEnhanced
                    fill
                    v-else-if="comp.component === 'select-enhanced'"
                    :label="comp.props.label"
                    :hint="comp.props.hint"
                    :tooltip="comp.props.tooltip"
                    :options="comp.props.options"
                    :searchable="comp.props.searchable"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <TextInput
                    fill
                    v-else-if="comp.component === 'textinput'"
                    :label="comp.props.label"
                    :hint="comp.props.hint"
                    :placeholder="comp.props.placeholder"
                    :tooltip="comp.props.tooltip"
                    :mask="comp.props.mask"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <TextareaInput
                    fill
                    v-else-if="comp.component === 'textarea'"
                    :label="comp.props.label"
                    :hint="comp.props.hint"
                    :tooltip="comp.props.tooltip"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <RadioGroup
                    v-else-if="comp.component === 'radio-group'"
                    :label="comp.props.label"
                    :tooltip="comp.props.tooltip"
                    :options="comp.props.options"
                    :classes="comp.props.classes"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <CheckboxGroup
                    v-else-if="comp.component === 'checkbox-group'"
                    :label="comp.props.label"
                    :tooltip="comp.props.tooltip"
                    :dynamic="comp.props.dynamic"
                    :dynamic-label="comp.props.dynamicLabel"
                    :modal="comp.props.modal"
                    :force-unique="comp.props.forceUnique"
                    :options="comp.props.options"
                    :classes="comp.props.classes"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <RadioMatrix
                    v-else-if="comp.component === 'radio-matrix'"
                    :choices="comp.props.choices"
                    :dynamic="comp.props.dynamic"
                    :dynamic-label="comp.props.dynamicLabel"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <Datepicker
                    v-else-if="comp.component === 'datepicker'"
                    :label="comp.props.label"
                    :hint="comp.props.hint"
                    :tooltip="comp.props.tooltip"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :minus-years="(comp.props || {}).minusYears || 0"
                    :value.sync="fieldvalues[comp.id]"
                   />
                  <RadioMatrixAdvanced
                    v-else-if="comp.component === 'radio-matrix-advanced'"
                    :choices="comp.props.choices"
                    :dynamic="comp.props.dynamic"
                    :dynamic-label="comp.props.dynamicLabel"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                  />
                  <DiagnosisInput
                    v-else-if="comp.component === 'diagnosis'"
                    :types="comp.props.types"
                    :simplified="comp.props.simplified"
                    :diagnoses="comp.props.diagnoses"
                    :codes="comp.props.codes"
                    :dynamic="comp.props.dynamic"
                    :dynamic-label="comp.props.dynamicLabel"
                    :for-state="comp.for_state"
                    :for-report="comp.for_report"
                    :value.sync="fieldvalues[comp.id]"
                    :data-version="version"
                  />
                  <Spacer v-else-if="comp.component === 'spacer'" />
                  <div
                    v-if="isReviewing && !['spacer', 'survey'].includes(comp.component)"
                    class="comment"
                    :class="{ 'has-comment': !!comments[comp.id] }"
                    :title="comments[comp.id] ? 'Kommentar editieren' : 'Kommentar erfassen'"
                    @click="handleComment(comp.id)"
                  >
                    <Icon name="comment" />
                  </div>
                </div>
            </div>

            <!--
            <Survey
              :checked="surveyDone"
              @input="updateSurveyState($event)"
            />
            -->

            <div class="section-actions">
              <Button
                :disabled="!canNavigateBack"
                @click="prevSection">
                  Vorherige Seite
              </Button>
              <Button
                :disabled="!canNavigateForward"
                @click="nextSection">
                  Nächste Seite
              </Button>
            </div>
          </template>
        </div>
      </div>
      <div class="case-actions">
        <Button
          v-for="(action, idx) in computedNavActions"
          :key="`selectiveAction-${idx}`"
          :secondary="!action.primary"
          :disabled="action.disabled"
          :primary="action.primary === true"
          @click="handleSelectiveAction(action.value)"
        >
          {{ action.label }}
        </Button>
      </div>
    </div>
  </main>
</template>

<script>
  import _ from 'lodash';
  import InteractionBar from '@/components/ui/inputs/InteractionBar';
  import ToggleBar from '@/components/ui/inputs/ToggleBar';
  import ProgressCircle from '@/components/ui/ProgressCircle';
  import RadioGroup from '@/components/ui/inputs/groups/RadioGroup';
  import TextareaInput from '@/components/ui/inputs/TextareaInput';
  import RadioMatrix from '@/components/ui/inputs/RadioMatrix';
  import DiagnosisInput from '@/components/ui/inputs/DiagnosisInput';
  import RadioMatrixAdvanced from '@/components/ui/inputs/RadioMatrixAdvanced';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Select from '@/components/ui/inputs/Select';
  import SelectEnhanced from '@/components/ui/inputs/SelectEnhanced';
  import CheckboxGroup from '@/components/ui/inputs/groups/CheckboxGroup';
  import Datepicker from '@/components/ui/inputs/Datepicker';
  import Button from '@/components/ui/inputs/Button';
  import Icon from '@/components/ui/Icon';
  import Dropdown from '@/components/ui/inputs/Dropdown';
  import InquiryProgressExtended from '@/components/inquiry/InquiryProgressExtended';
  import Tooltip from '@/components/ui/Tooltip';
  import UndoRedoSave from '@/components/inquiry/UndoRedoSave';
  import InquiryStatus from '@/objects/InquiryStatus';
  import InquiryActions from '@/objects/InquiryActions';
  import ModalInquiryReview from '@/components/modals/ModalInquiryReview';
  import ModalInquiryReturn from '@/components/modals/ModalInquiryReturn';
  import ModalInquiryClosed from '@/components/modals/ModalInquiryClosed';
  import ModalInquiryDelete from '@/components/modals/ModalInquiryDelete';
  import ModalInquiryArchive from '@/components/modals/ModalInquiryArchive';
  import ModalInquiryTransfer from '@/components/modals/ModalInquiryTransfer';
  import ModalInquiryDownload from '@/components/modals/ModalInquiryDownload';
  import ModalInquiryConvert from '@/components/modals/ModalInquiryConvert';
  import ModalInquiryComment from '@/components/modals/ModalInquiryComment';
  import ModalInquiryConflict from '@/components/modals/ModalInquiryConflict';
  import InquiryDefinitionFunctions from '@/components/inquiry/InquiryDefinitionFunctions';
  import InquiryProgressValidators from '@/components/inquiry/InquiryProgressValidators';
  import Spacer from '@/components/ui/Spacer';
  // import Survey from '@/components/ui/Survey';
  import Toast from '@/components/ui/Toast';
  // import ModalInquirySurvey from '@/components/modals/ModalInquirySurvey';
  import { Store } from '@/store';
  import Crypto from '@/utils/Crypto';
  import DBStorage from '@/utils/DBStorage';
  import VueUtil from '@/utils/VueUtil';

  //----------------------------------
  // Constants
  //----------------------------------
  const FILTER_ALL = 'all';
  const FILTER_STATE = 'state';
  const FILTER_REPORT = 'report';

  const CURRENT_DATA_VERSION = 2;

  export default {
    name: 'Inquiry',
    components: {
      // Survey,
      Spacer,
      Tooltip,
      InquiryProgressExtended,
      Dropdown,
      Icon,
      Button,
      Datepicker,
      CheckboxGroup,
      Select,
      SelectEnhanced,
      TextInput,
      RadioMatrixAdvanced,
      DiagnosisInput,
      RadioMatrix,
      TextareaInput,
      RadioGroup,
      ProgressCircle,
      InteractionBar,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      uuid: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        iuuid: null,
        inquiryid: null,
        version: 2,
        inquiryStatus: InquiryStatus.OPEN,

        fielddata: null,
        fieldvalues: {},
        comments: {},
        oldFieldValues: {},

        loadedInquiry: null,

        saveStatus: null,
        savePromise: null,
        saveTime: null,
        time: null,

        undos: [],
        redos: [],

        activeSection: 0,
        activeFilter: FILTER_REPORT,

        readOnly: false,

        totalFieldsProgress: 0,

        preventFieldValueChange: false,
        preventRouteLeave: false,
        saveTimeout: null,
        storeTimeout: null,

        // surveyDone: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      interactions() {
        const labels = this.$i18n.t('inquiry.filters');
        return [
          {
            id: 'field-filter',
            type: ToggleBar,
            props: {
              secondary: true,
              value: FILTER_REPORT,
              options: [
                /* { value: FILTER_ALL, label: labels.all, icon: 'fieldsAll' }, */
                { value: FILTER_REPORT, label: labels.report, icon: 'fieldsSAV' },
                { value: FILTER_STATE, label: labels.state, icon: 'fieldsState' },
              ],
            },
          },
          {
            id: 'undoredosave',
            type: UndoRedoSave,
            props: {
              status: this.saveStatus,
              time: this.saveTime,
              undos: this.undos,
              redos: this.redos,
            },
          },
        ];
      },
      computedSections() {
        const fielddata = this.fielddata;
        const filter = this.activeFilter;
        if (fielddata) {
          const sections = [];
          fielddata.forEach((item) => {
            if (item.type === 'section') {
              const inputs = this.getAllSectionInputs(item);
              const numInputs = inputs.filter(input => (filter === FILTER_REPORT && (input.for_report || input.for_state)) || (filter === FILTER_STATE && input.for_state));
              if (filter === FILTER_ALL || (filter && numInputs.length > 0)) {
                sections.push({
                  index: item.index,
                  label: item.label,
                  progress: item.progress || 0,
                  noProgress: item.noProgress,
                  numInputs,
                });
              }
            }
          });
          return sections;
        }
        return [];
      },
      computedSectionHeading() {
        const section = (this.fielddata || []).find(s => s.index === this.activeSection) || null;
        return (section?.heading) ? section.heading : null;
      },
      computedSectionTooltip() {
        const section = (this.fielddata || []).find(s => s.index === this.activeSection) || null;
        return (section?.tooltip) ? section.tooltip : null;
      },
      computedSectionGroups() {
        const section = (this.fielddata || []).find(s => s.index === this.activeSection) || {};
        if (section?.children) {
          const out = _.cloneDeep(section.children);
          if (this.activeFilter !== FILTER_ALL) {
            const filter = this.activeFilter;
            out.forEach((group) => {
              // eslint-disable-next-line no-param-reassign
              group.children = group.children.filter((comp) => {
                if (filter === FILTER_REPORT && (comp.for_report || comp.for_state)) {
                  return true;
                }
                return filter === FILTER_STATE && comp.for_state;
              });
            });
          }
          return out;
        }
        return [];
      },
      computedCaseinfo() {
        const reqs = this.$i18n.t('req');
        const values = this.fieldvalues;
        const f = values[reqs.f.v] || null;
        const l = values[reqs.l.v] || null;
        const d = values[reqs.d.v] || null;
        const g = values[reqs.g.v] || null;
        const out = {
          name: 'Unbekannt',
          date: '??.??.????',
          icon: 'anonymous',
        };
        if (f && l) {
          out.name = `${f} ${l}`;
        }
        if (d) {
          const [year, month, day] = d.split('-');
          out.date = `${day}.${month}.${year}`;
        }
        if (g === 'm') {
          out.icon = 'boy';
        } else if (g === 'w') {
          out.icon = 'girl';
        }
        return out;
      },
      computedInquiryActions() {
        if (this.canOnlyCreateStubInquiry) {
          return [];
        }
        const actions = InquiryActions.byStatus(this.inquiryStatus);
        return actions.filter(a => a.disabled === false).map(a => ({ ...a, value: a.action }));
      },
      computedNavActions() {
        if (this.canOnlyCreateStubInquiry) {
          return [];
        }
        const actions = this.$i18n.t('inquiry.navAction') || [];
        actions[actions.length - 1].primary = true;
        return actions;
      },
      canNavigateForward() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const nextSection = sections[foundIndex + 1] || null;
        return nextSection !== null;
      },
      canNavigateBack() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const prevSection = sections[foundIndex - 1] || null;
        return prevSection !== null;
      },
      canOnlyCreateStubInquiry() {
        const permission = Store.permissions;
        return !permission.inquiryCanCreateFull && permission.inquiryCanCreateStub;
      },
      isReviewing() {
        return this.inquiryStatus === InquiryStatus.REVIEWING;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      activeSection() {
        this.$nextTick(() => {
          const { formContainer } = this.$refs;
          const rect = formContainer.getBoundingClientRect();
          const scrollTop = document.documentElement.scrollTop;
          window.scrollTo({ left: 0, top: (rect.top + scrollTop), behavior: 'smooth' });
        });
      },
      computedSections(sections) {
        let index = this.activeSection;
        let found = false;
        let newIndex = null;
        while (!found) {
          // eslint-disable-next-line no-loop-func
          const section = sections.find(s => s.index === index);
          if (section) {
            newIndex = section.index;
            found = true;
          } else if (index > 0) {
            index -= 1;
          } else {
            newIndex = 0;
            found = true;
          }
        }
        this.activeSection = newIndex;
      },
      fieldvalues: {
        async handler() {
          if (!this.preventFieldValueChange) {
            clearTimeout(this.saveTimeout);
            const values = await this.prepareSaveData();
            await this.storeToIndexedDB(values);
            this.saveTimeout = setTimeout(() => { this.saveData(values); }, 1750);
          }
          this.updateSectionsProgress();
        },
        deep: true,
      },
      comments: {
        async handler() {
          if (!this.preventFieldValueChange) {
            clearTimeout(this.saveTimeout);
            const values = await this.prepareSaveData();
            await this.storeToIndexedDB(values);
            this.saveTimeout = setTimeout(() => { this.saveData(values); }, 1750);
          }
          this.updateSectionsProgress();
        },
        deep: true,
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      const uuid = this.uuid;
      let inquiry = null;

      if (uuid) {
        if (this.isValidUUID(uuid)) {
          inquiry = await this.$sav.api.get(`/inquiry/${uuid}`);
          if (inquiry) {
            this.iuuid = uuid;
            this.inquiryid = inquiry.inquiryid;
            this.inquiryStatus = inquiry.status;
            this.version = inquiry?.version || CURRENT_DATA_VERSION;
            this.saveTime = new Date(inquiry.modified);
            this.saveStatus = 'idle';
            // this.surveyDone = inquiry?.extra?.survey2025 || false;
          }
        } else {
          this.preventRouteLeave = true;
          Toast.warn('<strong>Ungültige Fall-URL.</strong><br/> Sie werden in wenigen Sekunden zur Übersicht weitergeleitet.', 6000);
          setTimeout(() => { this.$router.replace('/inquiries'); }, 6000);
          return;
        }
      }

      if ([InquiryStatus.CLOSED, InquiryStatus.ARCHIVED].includes(this.inquiryStatus)) {
        this.readOnly = true;
      }

      if (inquiry && this.version === 1 && !this.readOnly) {
        const modal = VueUtil.createComponent(ModalInquiryConvert, document.body, {
          propsData: {
            inquiryid: inquiry.inquiryid,
          },
          parent: this,
        });
        modal.$once('convert', async () => {
          this.version = CURRENT_DATA_VERSION;
          inquiry = this.convertOutdatedInquiry(inquiry);
          this.populateData(inquiry);
        });
        modal.$once('readonly', async () => {
          this.readOnly = true;
          this.populateData(inquiry);
        });
      } else {
        this.loadedInquiry = inquiry;
        const localData = await DBStorage.get(this.iuuid);
        const localVersion = ((localData || {}).versions || [])[0] || null;
        if (localVersion && localVersion.hash !== inquiry.hash) {
          this.resolveDataConflict(localData, inquiry, true);
        } else {
          this.populateData(inquiry);
        }
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    async beforeRouteLeave(to, from, next) {
      if (!this.preventRouteLeave) {
        clearTimeout(this.saveTimeout);
        if (!this.readOnly) {
          this.processSaveAndExit(next);
        } else {
          this.backToList();
        }
      } else {
        next();
      }
    },
    // async beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // UI Methods
      //----------------------------------
      setActiveSection(idx) {
        this.activeSection = idx;
      },
      nextSection() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const nextSection = sections[foundIndex + 1] || null;
        if (nextSection) {
          this.setActiveSection(nextSection.index);
        }
      },
      prevSection() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const prevSection = sections[foundIndex - 1] || null;
        if (prevSection) {
          this.setActiveSection(prevSection.index);
        }
      },
      backToList() {
        this.preventRouteLeave = true;
        this.$router.back();
      },
      async executeAction(action) {
        switch (action) {
          case InquiryActions.REVIEW: {
            const modal = VueUtil.createComponent(ModalInquiryReview, document.body, {
              propsData: {
                uuid: this.iuuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.RETURN_REVIEW: {
            const modal = VueUtil.createComponent(ModalInquiryReturn, document.body, {
              propsData: {
                uuid: this.iuuid,
              },
              parent: this,
            });
            modal.$once('done', async () => {
              await DBStorage.delete(this.iuuid);
              this.backToList();
            });
            break;
          }
          case InquiryActions.CLOSE: {
            const modal = VueUtil.createComponent(ModalInquiryClosed, document.body, {
              propsData: {
                uuid: this.iuuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.ARCHIVE: {
            const modal = VueUtil.createComponent(ModalInquiryArchive, document.body, {
              propsData: {
                uuid: this.iuuid,
              },
              parent: this,
            });
            modal.$once('done', async () => {
              await DBStorage.delete(this.iuuid);
              this.backToList();
            });
            break;
          }
          case InquiryActions.TRANSFER: {
            const modal = VueUtil.createComponent(ModalInquiryTransfer, document.body, {
              propsData: {
                uuid: this.iuuid,
                isAssigned: true,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.DOWNLOAD: {
            VueUtil.createComponent(ModalInquiryDownload, document.body, {
              propsData: {
                uuid: this.iuuid,
                savId: this.inquiryid,
                version: this.version,
                structure: this.fielddata,
                values: this.fieldvalues,
              },
              parent: this,
            });
            break;
          }
          case InquiryActions.DELETE: {
            const modal = VueUtil.createComponent(ModalInquiryDelete, document.body, {
              propsData: {
                uuid: this.iuuid,
              },
              parent: this,
            });
            modal.$once('done', async () => {
              await DBStorage.delete(this.iuuid);
              this.backToList();
            });
            break;
          }
          case InquiryActions.SAVE: {
            const values = await this.prepareSaveData();
            await this.storeToIndexedDB(values);
            await this.saveData(values);
            break;
          }
          case InquiryActions.SAVECLOSE: {
            this.processSaveAndExit();
            break;
          }
          default: {
            if (action) {
              console.log(action);
            }
          }
        }
      },

      async processSaveAndExit(next = null) {
        const onFailure = () => {
          if (next) next(false);
        };

        /*
        if (this.activeSection === 10 && !this.surveyDone) {
          const modal = VueUtil.createComponent(ModalInquirySurvey, document.body, { parent: this });
          modal.$once('cancel', async () => {
            const saved = await this.saveData();
            if (saved) this.backToList();
            else onFailure();
          });
          modal.$once('survey', async () => {
            const saved = await this.updateSurveyState(true);
            if (saved) this.backToList();
            else onFailure();
          });
        } else {
          // default behaviour
        }
        */
        const values = await this.prepareSaveData();
        await this.storeToIndexedDB(values);
        const saved = await this.saveData(values);
        if (saved) this.backToList();
        else onFailure();
      },

      //----------------------------------
      // Data Methods
      //----------------------------------
      async populateData(inquiry) {
        const constructedData = await this.constructFieldData();
        if (this.canOnlyCreateStubInquiry) {
          const { data } = await this.constructFieldData(true);
          constructedData.data = data;
        }

        this.preventFieldValueChange = true;

        const combinedValues = {
          ...constructedData.values,
        };

        if (inquiry) {
          let inquiryData = inquiry.data;
          if (inquiry.status === InquiryStatus.REVIEWING) {
            const review = inquiry.reviewdata?.data || {};
            this.comments = inquiry.reviewdata?.comments || {};
            if (Object.keys(review).length > 0) {
              inquiryData = review;
            }
          }
          const valueKeys = Object.keys(combinedValues);
          valueKeys.forEach((key) => {
            const value = inquiryData[key] || null;
            if ((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && value)) {
              combinedValues[key] = value;
            }
          });
        }

        this.fieldvalues = combinedValues;
        this.oldFieldValues = this.cloneObject(this.fieldvalues);
        this.fielddata = constructedData.data;
        this.$nextTick(() => {
          this.preventFieldValueChange = false;
        });
      },

      async constructFieldData(stubOnly = false) {
        const fielddata = this.$i18n.t(`fielddata_versions.${this.version}`);
        const values = {};

        let data = fielddata.filter(section => !stubOnly || (stubOnly && section.stub));
        data = data.map((section) => {
          // eslint-disable-next-line no-param-reassign
          section.children = section.children.filter((group) => {
            // eslint-disable-next-line no-param-reassign
            group.children = group.children.filter(comp => !stubOnly || (stubOnly && comp.stub));
            return group.children.length > 0;
          });
          return section;
        });

        for (let s = 0, slen = data.length; s < slen; s += 1) {
          const section = data[s];
          section.index = s;
          const sectionChildren = section?.children || [];

          for (let c = 0, clen = sectionChildren.length; c < clen; c += 1) {
            const group = sectionChildren[c];
            const groupChildren = group?.children || [];

            for (let g = 0, glen = groupChildren.length; g < glen; g += 1) {
              const comp = groupChildren[g];
              if (comp.type === 'input') {
                values[comp.id] = comp?.props.value || null;
                if (comp?.props.valueFunc) {
                  const fn = InquiryDefinitionFunctions[comp.props.valueFunc];
                  // eslint-disable-next-line no-await-in-loop
                  values[comp.id] = await fn() || null;
                }

                if (comp?.props.optionsFunc) {
                  const fn = InquiryDefinitionFunctions[comp.props.optionsFunc];
                  if (fn) {
                    // eslint-disable-next-line no-await-in-loop
                    const { options, value } = await fn() || null;
                    if (options) {
                      comp.props.options = options;
                    }
                    if (value) {
                      values[comp.id] = value;
                    }
                  }
                }
              }
            }
          }
        }
        return { data, values };
      },

      async handleHistory(action) {
        if (['undo', 'redo'].includes(action)) {
          const currentData = this.cloneObject(this.fieldvalues);
          let newData = null;

          if (action === 'undo') {
            this.redos.push(currentData);
            newData = this.undos.pop();
          } else if (action === 'redo') {
            this.undos.push(currentData);
            newData = this.redos.pop();
          }

          if (newData) {
            this.preventFieldValueChange = true;
            this.fieldvalues = newData;
            this.$nextTick(() => {
              this.preventFieldValueChange = false;
            });
          }
        } else if (action === 'versions') {
          const inquiry = this.loadedInquiry;
          const localData = await DBStorage.get(this.iuuid);
          this.resolveDataConflict(localData, inquiry, false);
        }
      },

      //----------------------------------
      // Save Methods
      //----------------------------------
      async prepareSaveData(values = null) {
        // eslint-disable-next-line no-param-reassign
        values = values || JSON.parse(JSON.stringify(this.fieldvalues));
        if (!this.readOnly && this.satisfiesMinimumRequirements(values)) {
          try {
            const numFilledValues = Object.keys(values).map(key => values[key]).filter((val) => {
              if (val === null || val === '') {
                return false;
              }
              return !(val.constructor === Array && val.length === 0);
            });

            const inquiryStatus = this.inquiryStatus;
            const postData = {
              uuid: this.iuuid,
              status: inquiryStatus,
              data: values,
              version: this.version,
              comments: this.cloneObject(this.comments),
              hash: await Crypto.generateHash(values),
              modified: Math.floor(Date.now() / 1000),
              extra: {},
              /*
              extra: {
                survey2025: this.surveyDone,
              },
              */
            };

            if ([InquiryStatus.OPEN, InquiryStatus.PROGRESS].includes(inquiryStatus)) {
              if (numFilledValues.length > 7) {
                postData.status = InquiryStatus.PROGRESS;
              }
              if (this.totalFieldsProgress >= 1) {
                postData.status = InquiryStatus.PROGRESS_COMPLETE;
              }
            }
            return postData;
          } catch (e) {
            this.saveStatus = 'error';
            console.error(e);
          }
        }
        return null;
      },

      async saveData(values = null) {
        clearTimeout(this.saveTimeout);
        this.saveStatus = 'updating';
        this.readOnly = true;
        if (this.savePromise) {
          try {
            await this.savePromise;
          } catch (e) { /* ignore */ }
        }

        if (!values) {
          // eslint-disable-next-line no-param-reassign
          values = await this.prepareSaveData();
        }
        this.savePromise = this.performSave(values);
        const result = await this.savePromise;

        this.savePromise = null;
        this.readOnly = false;
        return result;
      },

      async performSave(values = null) {
        try {
          if (values) {
            const response = await this.$sav.api.post('/inquiry', null, values);
            if (response?.uuid) {
              if (this.iuuid !== response.uuid) {
                this.iuuid = response.uuid;
                this.inquiryid = response.inquiryid;

                // eslint-disable-next-line no-param-reassign
                values.uuid = response.uuid;
                await this.storeToIndexedDB(values);

                const scrollTop = document.documentElement.scrollTop;
                this.preventRouteLeave = true;
                this.$router.replace({ path: `/inquiry/${response.uuid}` });
                this.$nextTick(() => {
                  this.preventRouteLeave = false;
                  window.scrollTo({ left: 0, top: scrollTop, behavior: 'auto' });
                });
              }

              this.inquiryStatus = response.status;
              this.saveStatus = 'updated';
              this.saveTime = new Date(response.modified);
              this.undos.push(this.oldFieldValues);
              this.oldFieldValues = this.cloneObject(this.fieldvalues);
              return true;
            }
          }
        } catch (e) {
          this.saveStatus = 'error';
          console.error(e);
          return false;
        }
        return true;
      },

      convertOutdatedInquiry(inquiry) {
        return inquiry;
      },

      //----------------------------------
      // IndexedDB Methods
      //----------------------------------
      async storeToIndexedDB(obj) {
        const uuid = obj?.uuid;
        if (uuid) {
          const entry = await DBStorage.get(uuid) || {
            uuid,
            versions: [],
          };

          const threshold = 900; // 15 minutes
          const now = Math.floor(Date.now() / 1000);

          const newEntry = {
            data: await Crypto.encrypt(obj.data, Store.skey),
            hash: obj.hash,
            modified: now,
            modifier: Store.uuid,
          };

          const lastVersion = entry.versions[0];
          const lastModified = lastVersion?.modified || 0;
          const hasChanged = lastVersion?.hash !== newEntry.hash;
          const shouldCreateNewVersion = (lastModified + threshold < now) && hasChanged;

          if (entry.versions.length === 0 || shouldCreateNewVersion) {
            entry.versions.unshift(newEntry);
          } else {
            entry.versions[0] = newEntry;
          }

          if (entry.versions.length > 10) {
            entry.versions = entry.versions.slice(0, 10);
          }

          await DBStorage.set(entry);
        }
      },

      //----------------------------------
      // Data Conflict Methods
      //----------------------------------
      resolveDataConflict(localData, inquiry, warn = true) {
        this.readOnly = true;
        const latestRemoteVersion = {
          data: inquiry.data,
          modified: Math.floor(new Date(inquiry.modified).getTime() / 1000),
          modifier: inquiry.modifier?.uuid,
          local: false,
        };
        const remoteVers = (inquiry.versions || []).map(v => ({ ...v, local: false }));
        remoteVers.unshift(latestRemoteVersion);
        const localVers = (localData?.versions || []).map(v => ({ ...v, local: true }));
        const versions = [...remoteVers, ...localVers].sort((a, b) => b.modified - a.modified);

        const modal = VueUtil.createComponent(ModalInquiryConflict, document.body, { propsData: { warn, versions }, parent: this });
        modal.$once('resolve', (resp) => {
          const inq = this.cloneObject(inquiry);
          this.$nextTick(async () => {
            const { data, local } = resp;
            inq.data = !local ? data : await Crypto.decrypt(data, Store.skey);
            this.readOnly = false;
            this.populateData(inq);
          });
        });
        modal.$once('cancel', () => {
          this.populateData(inquiry);
        });
        modal.$on('change', async (resp) => {
          const inq = this.cloneObject(inquiry);
          this.$nextTick(async () => {
            const { data, local } = resp;
            inq.data = !local ? data : await Crypto.decrypt(data, Store.skey);
            this.populateData(inq);
          });
        });
      },

      //----------------------------------
      // Helper Methods
      //----------------------------------
      updateSectionsProgress() {
        const data = this.cloneObject(this.fielddata);
        const values = this.fieldvalues;
        let numSections = 0;
        let numCompletedSections = 0;

        data.forEach((section) => {
          let inputs = this.getAllSectionInputs(section);
          inputs = inputs.filter(input => input.for_state && input.required === true);
          const numInputs = inputs.length;
          let numCompletedInputs = 0;
          inputs.forEach((input) => {
            if (InquiryProgressValidators.isValid(input, values[input.id] || null)) {
              numCompletedInputs += 1;
            }
          });

          let progress = (numInputs > 0) ? numCompletedInputs / numInputs : 1;
          progress = (progress > 0 && progress < 1) ? 0.65 : progress;
          // eslint-disable-next-line no-param-reassign
          section.progress = progress;

          numSections += 1;
          if (progress >= 1) {
            numCompletedSections += 1;
          }
        });

        let allSectionsProgress = numCompletedSections / numSections;
        allSectionsProgress = (allSectionsProgress > 0 && allSectionsProgress < 1) ? 0.65 : allSectionsProgress;
        this.totalFieldsProgress = allSectionsProgress;
        this.fielddata = data;
      },

      getAllSectionInputs(section) {
        const inputs = [];
        section.children.forEach((group) => {
          group.children.forEach((child) => {
            if (child.type === 'input') {
              inputs.push(child);
            }
          });
        });
        return inputs;
      },

      cloneObject(obj) {
        return JSON.parse(JSON.stringify(obj));
      },

      satisfiesMinimumRequirements(values) {
        const reqs = this.$i18n.t('req');
        const reqf = Object.keys(reqs).filter(k => (reqs[k].r === true));
        const requirements = reqf.map(r => reqs[r].v).map(v => values[v]);
        return requirements.filter(r => r === '' || r === null).length === 0;
      },

      isValidUUID(uuid) {
        const rgx = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return rgx.test(uuid);
      },

      //----------------------------------
      // Survey
      //----------------------------------
      /*
      async updateSurveyState(bool) {
        this.surveyDone = bool;
        return this.saveData();
      },
      */

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleInteraction(data) {
        const { id, value } = data;
        if (id === 'field-filter') {
          this.activeFilter = value;
        }
        if (id === 'undoredosave') {
          this.handleHistory(value);
        }
      },
      handleInquiryAction(value) {
        const { actionDropdown } = this.$refs;
        actionDropdown.close();
        this.executeAction(value);
      },
      handleSelectiveAction(value) {
        this.executeAction(value);
      },
      handleComment(uuid) {
        const modal = VueUtil.createComponent(ModalInquiryComment, document.body, {
          propsData: { uuid, comment: this.comments[uuid] },
          parent: this,
        });
        modal.$once('save', async (comment) => {
          const comments = JSON.parse(JSON.stringify(this.comments));
          comments[uuid] = comment;
          this.comments = comments;
        });
        modal.$once('delete', async () => {
          const comments = JSON.parse(JSON.stringify(this.comments));
          delete comments[uuid];
          this.comments = comments;
        });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

main.view.inquiry {
  padding-top: 32px;

  .meta {
    height: 48px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    a {
      color: $color-black-alpha-60;
      text-transform: uppercase;
      text-decoration: none;
      svg {
        position: relative;
        top: 2px;
        width: 16px;
        height: 16px;
        margin-right: 8px;
      }
    }

    .caseinfo {
      height: 48px;
      border-radius: $default-border-radius;
      background-color: $color-white-solid-10;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 12px 24px;

      & > svg {
        width: 24px;
        height: 24px;
      }

      & > .name {
        margin-left: 12px;
        margin-right: 8px;
        @include font-body-medium;
        color: $color-black-solid-100;
      }
      & > .birthdate {
        @include font-body-small;
        color: $color-black-alpha-60;
      }
    }
  }
  .progress {
    display: flex;
    justify-content: center;
    padding: 32px 0;
  }
  .container {
    position: relative;
    background-color: $color-white-solid-10;
    box-shadow: $box-shadow-2;
    min-height: 48px;
    border-radius: $default-border-radius;

    & > .comp-interactionbar {
      width: 100%;
      position: sticky;
      top: 0;
      z-index: 3;
      justify-content: space-between;
      background-color: $color-white-solid-10;
      border-bottom: 1px solid $color-black-alpha-20;
    }

    & > .report-container {
      display: flex;
      padding: 32px;

      & > div:first-of-type {
        margin-right: 64px;

        ul {
          position: sticky;
          top: 105px;
          width: 276px;
          list-style-type: none;
          padding: 0;
          margin: 0;

          li {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-radius: $default-border-radius;
            background-color: $color-blue-alpha-20;
            padding: 12px 11px;
            margin-bottom: 4px;
            @include font-body-medium;
            color: $color-black-solid-100;
            user-select: none;
            cursor: pointer;

            & > span { padding-right: 8px; }
            & > .comp-progress-circle { min-width: 24px; }

            &:last-of-type {
              margin-bottom: 0;
            }

            &.active {
              cursor: default;
              background-color: transparent;
              @include font-body-medium-bold;
            }
          }
        }
      }
      & > div:last-of-type {
        width: 100%;
        // max-width: 956px;
        h1 {
          margin-bottom: 56px;
          .comp-tooltip {
            position: relative;
            top: -2px;
            & > svg {
              width: 22px;
              height: 22px;
            }
          }
        }
        h3 {
          margin-bottom: 24px;
          & > svg {
            margin: 0 6px;
            display: inline-block;
            position: relative;
            top: 1px;
            max-width: 14px;
            max-height: 14px;
          }
        }

        .group {
          padding: 36px 0 24px 0;
          border-bottom: 1px solid $color-black-alpha-10;

          &:empty {
            display: none;
          }

          &:first-of-type {
            padding-top: 0;
          }
          &:last-of-type {
            padding-bottom: 0;
            border-bottom: 0;
          }

          & > * {
            margin-bottom: 24px;

            &:has(.comment) {
              display: flex;
              gap: 16px;
              justify-content: space-between;
              align-items: flex-end;
              & > *:first-child {
                width: 100%;
              }
              & > .comment {
                position: relative;
                cursor: pointer;
                min-width: 32px;
                min-height: 32px;
                width: 32px;
                height: 32px;

                & > svg {
                  width: 32px;
                  height: 32px;
                  path {
                    color: currentColor;
                  }
                }

                &.has-comment {
                  & > svg path {
                    color: black;
                  }
                  &:after {
                    right: -4px;
                    top: 0;
                    content: '';
                    position: absolute;
                    width: 12px;
                    height: 12px;
                    border-radius: 100%;
                    background-color: $color-signal-red;
                  }
                }
              }
            }
          }
        }

        .section-actions {
          display: flex;
          justify-content: center;
          padding: 38px 0 0;

          button {
            margin-right: 16px;
            &:last-of-type { margin-right: 0; }
          }
        }

        &.readonly {
          pointer-events: none;
        }
      }
    }

    & > .case-actions {
      display: flex;
      justify-content: flex-end;
      padding: 17px 20px;
      border-top: 1px solid $color-black-alpha-10;

      button {
        margin-right: 16px;
        &:last-of-type { margin-right: 0; }
      }
    }
  }
}
</style>
